import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Bug,
  Clock,
  CheckCircle2,
  Sparkles,
  Trash2,
  UserCheck,
  UserX,
  ChevronDown,
  X,
  AlertTriangle,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { Issue, IssueStatus, UserProfile } from "../types";
import { formatSlaCountdown } from "../utils/slaCalculator";
import { getAppPillClasses } from "../utils/appColors";
import { CategoryBadge } from "./CategoryBadge";
import { hasUnreadResponse } from "../utils/notificationTracker";

export interface IssueListViewProps {
  issues: Issue[];
  onSelectIssue: (issue: Issue) => void;
  onCreateIssue?: () => void;
  currentUser?: UserProfile;
  adminUsers?: UserProfile[];
  onBulkStatusChange?: (
    issueIds: string[],
    newStatus: IssueStatus,
  ) => Promise<void>;
  onBulkAssign?: (
    issueIds: string[],
    assignee: { uid: string; email: string; displayName: string } | null,
  ) => Promise<void>;
  onBulkDelete?: (issueIds: string[]) => Promise<void>;
  onDeleteIssue?: (issueId: string) => Promise<void>;
}

export type IssueSortField =
  | "id"
  | "category"
  | "appName"
  | "priority"
  | "status"
  | "assignee"
  | "createdAt";

export type SortDirection = "asc" | "desc";

const PRIORITY_ORDER: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const STATUS_ORDER: Record<IssueStatus, number> = {
  new: 1,
  in_review: 2,
  in_progress: 3,
  pending_user_feedback: 4,
  resolved: 5,
  closed: 6,
};

const STATUS_OPTIONS: { label: string; value: IssueStatus; color: string }[] = [
  { label: "New", value: "new", color: "bg-blue-100 text-blue-800" },
  {
    label: "In Review",
    value: "in_review",
    color: "bg-purple-100 text-purple-800",
  },
  {
    label: "In Progress",
    value: "in_progress",
    color: "bg-teal-100 text-teal-800",
  },
  {
    label: "Pending Feedback",
    value: "pending_user_feedback",
    color: "bg-amber-100 text-amber-800",
  },
  {
    label: "Resolved",
    value: "resolved",
    color: "bg-emerald-100 text-emerald-800",
  },
  { label: "Closed", value: "closed", color: "bg-slate-100 text-slate-700" },
];

export const IssueListView: React.FC<IssueListViewProps> = ({
  issues,
  onSelectIssue,
  onCreateIssue,
  currentUser,
  adminUsers = [],
  onBulkStatusChange,
  onBulkAssign,
  onBulkDelete,
  onDeleteIssue,
}) => {
  const [selectedIssueIds, setSelectedIssueIds] = useState<Set<string>>(
    new Set(),
  );
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [singleDeleteTarget, setSingleDeleteTarget] = useState<Issue | null>(
    null,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const statusMenuRef = useRef<HTMLDivElement | null>(null);
  const assigneeMenuRef = useRef<HTMLDivElement | null>(null);
  const isStaff =
    currentUser?.role === "it_admin" || currentUser?.role === "dept_admin";
  const isItAdmin = currentUser?.role === "it_admin";

  const [sortField, setSortField] = useState<IssueSortField>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: IssueSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(
        field === "createdAt" || field === "priority" ? "desc" : "asc",
      );
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLTableCellElement>,
    field: IssueSortField,
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSort(field);
    }
  };

  const getAriaSort = (
    field: IssueSortField,
  ): "ascending" | "descending" | "none" => {
    if (sortField !== field) return "none";
    return sortDirection === "asc" ? "ascending" : "descending";
  };

  const renderSortIcon = (field: IssueSortField) => {
    if (sortField === field) {
      return sortDirection === "asc" ? (
        <ArrowUp
          className="w-3.5 h-3.5 text-teal-600 shrink-0"
          aria-hidden="true"
        />
      ) : (
        <ArrowDown
          className="w-3.5 h-3.5 text-teal-600 shrink-0"
          aria-hidden="true"
        />
      );
    }
    return (
      <ArrowUpDown
        className="w-3.5 h-3.5 text-slate-400/50 group-hover:text-slate-600 transition-colors shrink-0"
        aria-hidden="true"
      />
    );
  };

  const sortedIssues = useMemo(() => {
    return [...issues].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "id":
          comparison = a.id.localeCompare(b.id, undefined, {
            numeric: true,
            sensitivity: "base",
          });
          if (comparison === 0) {
            comparison = a.title.localeCompare(b.title);
          }
          break;
        case "category":
          comparison = a.category.localeCompare(b.category);
          break;
        case "appName":
          comparison = a.appName.localeCompare(b.appName);
          break;
        case "priority": {
          const rankA = PRIORITY_ORDER[a.priority] ?? 0;
          const rankB = PRIORITY_ORDER[b.priority] ?? 0;
          comparison = rankA - rankB;
          break;
        }
        case "status": {
          const rankA = STATUS_ORDER[a.status] ?? 0;
          const rankB = STATUS_ORDER[b.status] ?? 0;
          comparison = rankA - rankB;
          break;
        }
        case "assignee": {
          const labelA = a.assigneeEmail
            ? (a.assigneeEmail.split("@")[0] ?? "").toLowerCase()
            : "zz_unassigned";
          const labelB = b.assigneeEmail
            ? (b.assigneeEmail.split("@")[0] ?? "").toLowerCase()
            : "zz_unassigned";
          comparison = labelA.localeCompare(labelB);
          break;
        }
        case "createdAt":
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      if (comparison === 0) {
        comparison = a.id.localeCompare(b.id);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [issues, sortField, sortDirection]);

  // Reconcile selection when issues change (remove IDs no longer in list)
  useEffect(() => {
    setSelectedIssueIds((prev) => {
      const validIds = new Set(issues.map((i) => i.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [issues]);

  // Set indeterminate state on select-all checkbox
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate =
        selectedIssueIds.size > 0 && selectedIssueIds.size < issues.length;
    }
  }, [selectedIssueIds, issues.length]);

  // Close menus on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (statusMenuRef.current && !statusMenuRef.current.contains(target)) {
        setStatusDropdownOpen(false);
      }
      if (
        assigneeMenuRef.current &&
        !assigneeMenuRef.current.contains(target)
      ) {
        setAssigneeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Re-render when read receipts update
  const [, setReadTick] = useState(0);
  useEffect(() => {
    const handler = () => setReadTick((prev) => prev + 1);
    window.addEventListener("mlc-ticket-read-updated", handler);
    return () => window.removeEventListener("mlc-ticket-read-updated", handler);
  }, []);

  const handleToggleSelectAll = () => {
    if (selectedIssueIds.size === issues.length && issues.length > 0) {
      setSelectedIssueIds(new Set());
    } else {
      setSelectedIssueIds(new Set(issues.map((i) => i.id)));
    }
  };

  const handleToggleIssue = (id: string) => {
    setSelectedIssueIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApplyStatus = async (status: IssueStatus) => {
    if (!onBulkStatusChange || selectedIssueIds.size === 0) return;
    setStatusDropdownOpen(false);
    setIsProcessing(true);
    setActionError(null);
    try {
      await onBulkStatusChange(Array.from(selectedIssueIds), status);
      setSelectedIssueIds(new Set());
    } catch (err: unknown) {
      console.error("Failed to update status in bulk:", err);
      setActionError("Failed to update ticket status. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApplyAssignee = async (
    assignee: { uid: string; email: string; displayName: string } | null,
  ) => {
    if (!onBulkAssign || selectedIssueIds.size === 0) return;
    setAssigneeDropdownOpen(false);
    setIsProcessing(true);
    setActionError(null);
    try {
      await onBulkAssign(Array.from(selectedIssueIds), assignee);
      setSelectedIssueIds(new Set());
    } catch (err: unknown) {
      console.error("Failed to update assignee in bulk:", err);
      setActionError("Failed to update assignees. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSingleDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (singleDeleteTarget) {
      if (!onDeleteIssue && !onBulkDelete) return;
      setIsProcessing(true);
      setActionError(null);
      try {
        if (onDeleteIssue) {
          await onDeleteIssue(singleDeleteTarget.id);
        } else if (onBulkDelete) {
          await onBulkDelete([singleDeleteTarget.id]);
        }
        setSelectedIssueIds((prev) => {
          const next = new Set(prev);
          next.delete(singleDeleteTarget.id);
          return next;
        });
        setSingleDeleteTarget(null);
      } catch (err: unknown) {
        console.error("Failed to delete ticket:", err);
        setActionError("Failed to delete ticket. Please try again.");
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    if (!onBulkDelete || selectedIssueIds.size === 0) return;
    setIsProcessing(true);
    setActionError(null);
    try {
      await onBulkDelete(Array.from(selectedIssueIds));
      setSelectedIssueIds(new Set());
      setIsDeleteModalOpen(false);
    } catch (err: unknown) {
      console.error("Failed to delete tickets in bulk:", err);
      setActionError("Failed to delete tickets. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (issues.length === 0) {
    return (
      <div className="glass rounded-2xl p-12 text-center border border-dashed border-slate-300">
        <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-500 mx-auto flex items-center justify-center mb-3">
          <Bug className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-slate-800">No Issues Found</h3>
        <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
          No reports match the selected filters. Change filter criteria or
          submit a new ticket.
        </p>
        {onCreateIssue && (
          <button
            type="button"
            onClick={onCreateIssue}
            className="btn-animate mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-semibold shadow-md shadow-teal-600/20 hover:bg-teal-700"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Submit New Ticket</span>
          </button>
        )}
      </div>
    );
  }

  const selectedCount = selectedIssueIds.size;
  const isAllSelected = issues.length > 0 && selectedCount === issues.length;

  return (
    <div className="relative">
      {/* Error banner if bulk action failed */}
      {actionError && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 text-rose-500" />
            <span>{actionError}</span>
          </div>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="text-rose-500 hover:text-rose-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Table */}
      <div className="glass rounded-xl border border-slate-200 overflow-hidden shadow-xs">
        <div
          tabIndex={0}
          role="region"
          aria-label="Issues and tickets table"
          className="overflow-x-auto focus:outline-none focus:ring-1 focus:ring-teal-500"
        >
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-100/75 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider">
              <tr>
                {isStaff && (
                  <th className="py-2.5 px-2 w-9 text-center">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleSelectAll}
                      aria-label="Select all issues"
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer w-4 h-4"
                    />
                  </th>
                )}
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("id")}
                  onClick={() => handleSort("id")}
                  onKeyDown={(e) => handleKeyDown(e, "id")}
                  className="py-2.5 px-2.5 cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center gap-1 font-semibold text-slate-600 group">
                    <span>Ticket</span>
                    {renderSortIcon("id")}
                  </div>
                </th>
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("category")}
                  onClick={() => handleSort("category")}
                  onKeyDown={(e) => handleKeyDown(e, "category")}
                  className="py-2.5 px-2 cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center gap-1 font-semibold text-slate-600 group">
                    <span>Type</span>
                    {renderSortIcon("category")}
                  </div>
                </th>
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("appName")}
                  onClick={() => handleSort("appName")}
                  onKeyDown={(e) => handleKeyDown(e, "appName")}
                  className="py-2.5 px-2 cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center gap-1 font-semibold text-slate-600 group">
                    <span>Application</span>
                    {renderSortIcon("appName")}
                  </div>
                </th>
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("priority")}
                  onClick={() => handleSort("priority")}
                  onKeyDown={(e) => handleKeyDown(e, "priority")}
                  className="py-2.5 px-2 cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center gap-1 font-semibold text-slate-600 group">
                    <span>Priority &amp; SLA</span>
                    {renderSortIcon("priority")}
                  </div>
                </th>
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("status")}
                  onClick={() => handleSort("status")}
                  onKeyDown={(e) => handleKeyDown(e, "status")}
                  className="py-2.5 px-2 cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center gap-1 font-semibold text-slate-600 group">
                    <span>Status</span>
                    {renderSortIcon("status")}
                  </div>
                </th>
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("assignee")}
                  onClick={() => handleSort("assignee")}
                  onKeyDown={(e) => handleKeyDown(e, "assignee")}
                  className="py-2.5 px-2 cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center gap-1 font-semibold text-slate-600 group">
                    <span>Assignee</span>
                    {renderSortIcon("assignee")}
                  </div>
                </th>
                <th
                  scope="col"
                  tabIndex={0}
                  aria-sort={getAriaSort("createdAt")}
                  onClick={() => handleSort("createdAt")}
                  onKeyDown={(e) => handleKeyDown(e, "createdAt")}
                  className="py-2.5 px-2.5 text-right cursor-pointer select-none hover:bg-slate-200/60 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset"
                >
                  <div className="inline-flex items-center justify-end gap-1 font-semibold text-slate-600 group">
                    <span>Created</span>
                    {renderSortIcon("createdAt")}
                  </div>
                </th>
                {isItAdmin && (
                  <th className="py-2.5 px-2 text-right w-10">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {sortedIssues.map((issue) => {
                const isSelected = selectedIssueIds.has(issue.id);
                const slaCountdown = issue.sla
                  ? formatSlaCountdown(issue.sla.targetAssignTime)
                  : null;
                const isUnassigned = !issue.assigneeUid && !issue.assigneeEmail;

                return (
                  <tr
                    key={issue.id}
                    onClick={() => onSelectIssue(issue)}
                    className={`cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-teal-50/60 hover:bg-teal-50/90"
                        : isUnassigned
                          ? "bg-amber-50/30 hover:bg-amber-50/60"
                          : "hover:bg-slate-50/80"
                    } ${
                      isUnassigned
                        ? "border-l-4 border-l-amber-400"
                        : "border-l-4 border-l-transparent"
                    }`}
                  >
                    {/* Checkbox column */}
                    {isStaff && (
                      <td
                        className="py-2.5 px-2 w-9 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleIssue(issue.id)}
                          aria-label={`Select ticket ${issue.id}`}
                          className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer w-4 h-4"
                        />
                      </td>
                    )}

                    {/* Ticket ID & Title */}
                    <td className="py-2.5 px-2.5 max-w-[200px] sm:max-w-[260px] lg:max-w-xs">
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col items-start gap-0.5 shrink-0">
                          <span className="font-mono font-bold text-[11px] text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-200">
                            {issue.id}
                          </span>
                          {hasUnreadResponse(issue, currentUser) && (
                            <span
                              data-testid={`unread-badge-${issue.id}`}
                              title="New response received"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-rose-100 text-rose-700 border border-rose-200 shadow-2xs animate-in fade-in duration-150"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                              <span>New Response</span>
                            </span>
                          )}
                        </div>
                        <div className="overflow-hidden min-w-0">
                          <p className="font-semibold text-slate-900 truncate">
                            {issue.title}
                          </p>
                          <p className="text-[11px] text-slate-500 truncate">
                            by {issue.reporterName}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Category Type */}
                    <td className="py-2.5 px-2 whitespace-nowrap">
                      <CategoryBadge category={issue.category} size="sm" />
                    </td>

                    {/* App */}
                    <td className="py-2.5 px-2 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border tracking-wide shadow-2xs ${getAppPillClasses(issue.appName)}`}
                      >
                        {issue.appName}
                      </span>
                    </td>

                    {/* Priority & SLA */}
                    <td className="py-2.5 px-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`px-1.5 py-0.5 rounded font-semibold uppercase text-[10px] ${
                            issue.priority === "critical"
                              ? "bg-rose-100 text-rose-800"
                              : issue.priority === "high"
                                ? "bg-amber-100 text-amber-800"
                                : issue.priority === "medium"
                                  ? "bg-sky-100 text-sky-800"
                                  : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {issue.priority}
                        </span>
                        {slaCountdown && !issue.assigneeUid && (
                          <span
                            className={`text-[10px] font-medium inline-flex items-center gap-1 ${
                              slaCountdown.isBreached
                                ? "text-rose-600 font-bold"
                                : slaCountdown.isWarning
                                  ? "text-amber-600"
                                  : "text-slate-500"
                            }`}
                          >
                            <Clock className="w-3 h-3" />
                            {slaCountdown.text}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-2.5 px-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded-full font-semibold text-[11px] inline-flex items-center gap-1 ${
                          issue.status === "new"
                            ? "bg-blue-100 text-blue-800"
                            : issue.status === "in_progress"
                              ? "bg-teal-100 text-teal-800"
                              : issue.status === "in_review"
                                ? "bg-purple-100 text-purple-800"
                                : issue.status === "resolved"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {issue.status === "resolved" && (
                          <CheckCircle2 className="w-3 h-3" />
                        )}
                        {issue.status.replace(/_/g, " ")}
                      </span>
                    </td>

                    {/* Assignee */}
                    <td className="py-2.5 px-2 whitespace-nowrap text-slate-600">
                      {issue.assigneeEmail ? (
                        <span className="font-medium text-slate-800">
                          {issue.assigneeEmail.split("@")[0]}
                        </span>
                      ) : (
                        <span
                          data-testid={`unassigned-badge-${issue.id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-900 border border-amber-300 shadow-2xs"
                        >
                          <UserX className="w-3 h-3 text-amber-600" />
                          <span>Unassigned</span>
                        </span>
                      )}
                    </td>

                    {/* Created Date */}
                    <td className="py-2.5 px-2.5 text-right text-slate-500 whitespace-nowrap">
                      {new Date(issue.createdAt).toLocaleDateString()}
                    </td>

                    {/* Actions (IT Admin only) */}
                    {isItAdmin && (
                      <td
                        className="py-2.5 px-2 text-right whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => setSingleDeleteTarget(issue)}
                          className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                          title={`Delete ticket ${issue.id}`}
                          aria-label={`Delete ticket ${issue.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Bulk Action Bar */}
      {isStaff && selectedCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-3 border border-slate-700/80 animate-in fade-in slide-in-from-bottom-4">
          <span className="text-xs font-semibold bg-teal-500/20 text-teal-300 px-2.5 py-1 rounded-full border border-teal-500/30 whitespace-nowrap">
            {selectedCount} {selectedCount === 1 ? "ticket" : "tickets"}{" "}
            selected
          </span>

          <div className="h-4 w-px bg-slate-700" />

          {/* Change Status Dropdown */}
          <div className="relative" ref={statusMenuRef}>
            <button
              type="button"
              disabled={isProcessing}
              onClick={() => {
                setStatusDropdownOpen((prev) => !prev);
                setAssigneeDropdownOpen(false);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-3.5 h-3.5 text-teal-400" />
              <span>Status</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {statusDropdownOpen && (
              <div className="absolute bottom-full mb-2 left-0 w-48 bg-white text-slate-800 rounded-xl shadow-xl border border-slate-200 py-1.5 z-50">
                <p className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Change Status To
                </p>
                {STATUS_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => void handleApplyStatus(opt.value)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 flex items-center justify-between transition-colors"
                  >
                    <span>{opt.label}</span>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${opt.color}`}
                    >
                      {opt.value}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Update Assignee Dropdown */}
          <div className="relative" ref={assigneeMenuRef}>
            <button
              type="button"
              disabled={isProcessing}
              onClick={() => {
                setAssigneeDropdownOpen((prev) => !prev);
                setStatusDropdownOpen(false);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition-colors disabled:opacity-50"
            >
              <UserCheck className="w-3.5 h-3.5 text-teal-400" />
              <span>Assignee</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {assigneeDropdownOpen && (
              <div className="absolute bottom-full mb-2 left-0 w-56 bg-white text-slate-800 rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 max-h-60 overflow-y-auto">
                <p className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Assign Tickets To
                </p>
                <button
                  type="button"
                  onClick={() => void handleApplyAssignee(null)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-100 text-amber-700 font-medium border-b border-slate-100 transition-colors"
                >
                  Unassigned
                </button>
                {adminUsers.map((admin) => (
                  <button
                    key={admin.uid}
                    type="button"
                    onClick={() =>
                      void handleApplyAssignee({
                        uid: admin.uid,
                        email: admin.email,
                        displayName: admin.displayName || admin.email,
                      })
                    }
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 flex items-center gap-2 transition-colors"
                  >
                    <div className="w-5 h-5 rounded-full bg-teal-100 text-teal-800 text-[10px] font-bold flex items-center justify-center shrink-0">
                      {(admin.displayName || admin.email)
                        .slice(0, 1)
                        .toUpperCase()}
                    </div>
                    <div className="overflow-hidden truncate">
                      <p className="font-medium text-slate-800 truncate">
                        {admin.displayName || admin.email}
                      </p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {admin.email}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Delete Button (IT Admin only, disabled for Dept Admin) */}
          <button
            type="button"
            disabled={!isItAdmin || isProcessing}
            onClick={() => setIsDeleteModalOpen(true)}
            title={
              isItAdmin
                ? "Delete selected tickets"
                : "Only IT Administrators can delete tickets"
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span>Delete</span>
          </button>

          <div className="h-4 w-px bg-slate-700" />

          {/* Clear Selection */}
          <button
            type="button"
            onClick={() => setSelectedIssueIds(new Set())}
            title="Clear selection"
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>

          {isProcessing && (
            <Loader2 className="w-4 h-4 text-teal-400 animate-spin" />
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {(isDeleteModalOpen || singleDeleteTarget !== null) && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150"
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-200 animate-in zoom-in-95 duration-150">
            <div className="flex items-center gap-3 text-rose-600 mb-3">
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                <Trash2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  {singleDeleteTarget
                    ? `Delete Ticket ${singleDeleteTarget.id}?`
                    : `Delete ${selectedCount} ${selectedCount === 1 ? "Ticket" : "Tickets"}?`}
                </h3>
                <p className="text-xs text-slate-500">
                  This action is permanent and cannot be undone.
                </p>
              </div>
            </div>

            <div className="my-4 p-3 bg-slate-50 rounded-xl border border-slate-200 max-h-40 overflow-y-auto">
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Selected Tickets:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {singleDeleteTarget ? (
                  <span className="font-mono text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded">
                    {singleDeleteTarget.id}
                  </span>
                ) : (
                  Array.from(selectedIssueIds).map((id) => (
                    <span
                      key={id}
                      className="font-mono text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded"
                    >
                      {id}
                    </span>
                  ))
                )}
              </div>
            </div>

            <p className="text-xs text-slate-600 mb-6">
              {singleDeleteTarget
                ? `Are you sure you want to permanently remove ticket "${singleDeleteTarget.title}" from the database? All associated comments and activities will be lost.`
                : "Are you sure you want to permanently remove these tickets from the database? All associated comments and activities will be lost."}
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                disabled={isProcessing}
                onClick={handleCloseDeleteModal}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isProcessing}
                onClick={() => void handleConfirmDelete()}
                className="btn-animate inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-md shadow-rose-600/20 disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Permanently Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
